import { gql } from '@apollo/client';

import { client as apolloClient } from '../gql/client';
import { mutateWithDeadline } from '../offline/mutationDeadline';
import {
  putWithProgress,
  UploadStalledError,
  type UploadProgress,
} from './uploadWithProgress';

/**
 * Storage helper — Hetzner Object Storage uploads via backend-issued
 * presigned PUT URLs. Backend enforces tenantId + entityType prefixes.
 *
 * Flow:
 *   1. requestUploadToken({ orderId, entityType, roidName, contentType })
 *      → backend returns { uploadUrl, storageKey, storageBucket, expiresAt }
 *   2. PUT blob to uploadUrl with the matching Content-Type
 *   3. Caller invokes createImage mutation with { storageKey, ... }
 *
 * Errors are surfaced as `HetznerUploadError` (defined below) — a distinct
 * class from Cloudinary's, carrying `httpStatus` (not `cloudinaryStatus`).
 * Contract for maintainers: the offline sync manager
 * (offlineImageUploadSyncManager.ts) special-cases this class — a presigned
 * PUT failure with `httpStatus` 4xx (403 expired/invalid, 400 malformed) is
 * treated as terminal (markAsFailed, no retry); 5xx/network stays retryable.
 */

export const REQUEST_UPLOAD_TOKEN = gql`
  mutation RequestUploadToken($input: RequestUploadTokenInput!) {
    requestUploadToken(input: $input) {
      uploadUrl
      storageKey
      storageBucket
      expiresAt
    }
  }
`;

export type EntityType =
  | 'accident'
  | 'action'
  | 'communication'
  | 'order'
  // Tramo del recorrido de vigilancia. Prefijo propio y NO `action`: la clave
  // es `tenants/<tenantId>/<entityType>/<yyyy>/<mm>/…`, así que colgar las
  // fotos de un tramo bajo `action/` mentiría en la ruta y estropearía
  // cualquier limpieza o auditoría por prefijo. El backend valida el segmento
  // con un regex (`SAFE_SEGMENT`), no con una lista cerrada → no hay que
  // tocarlo.
  | 'segment';

export interface UploadTokenInput {
  orderId: string;
  entityType: EntityType;
  roidName: string;
  contentType: string;
}

export interface UploadTokenResult {
  uploadUrl: string;
  storageKey: string;
  storageBucket: string;
  expiresAt: string;
}

export class HetznerUploadError extends Error {
  public readonly httpStatus?: number;
  constructor(message: string, httpStatus?: number) {
    super(message);
    this.name = 'HetznerUploadError';
    this.httpStatus = httpStatus;
  }
}

export async function requestUploadToken(
  input: UploadTokenInput,
): Promise<UploadTokenResult> {
  // Con plazo: sin él, un permiso sin respuesta paraba la foto y dejaba la
  // pantalla encendida sin fin. Al vencer falla como fallo de red y la cola lo
  // reintenta (pedir otro permiso no tiene efectos). Ver `mutationDeadline`.
  const { data } = await mutateWithDeadline<{
    requestUploadToken: UploadTokenResult | null;
  }>(apolloClient, {
    mutation: REQUEST_UPLOAD_TOKEN,
    variables: { input },
  });
  if (!data?.requestUploadToken) {
    throw new HetznerUploadError('Failed to obtain upload token from backend');
  }
  const token: UploadTokenResult = data.requestUploadToken;
  // Discriminated as a retryable upload failure by the offline flow, not an
  // uncaught exception: an empty storageKey means createImage would persist a
  // broken (unresolvable) Image record.
  if (!token.storageKey || token.storageKey.trim() === '') {
    throw new HetznerUploadError('backend returned empty storageKey');
  }
  return token;
}

export async function putToHetzner(
  uploadUrl: string,
  blob: Blob,
  contentType: string,
  onProgress?: (p: UploadProgress) => void,
): Promise<void> {
  try {
    await putWithProgress(uploadUrl, blob, contentType, { onProgress });
  } catch (err) {
    // Se conserva `HetznerUploadError` como tipo público: el sync manager
    // clasifica por `httpStatus` (4xx terminal, 5xx/red reintentable) y ese
    // contrato no debe cambiar por haber cambiado el transporte.
    //
    // El estancamiento viaja SIN status, igual que un error de red: es
    // reintentable, no un rechazo del servidor.
    if (err instanceof UploadStalledError) {
      throw new HetznerUploadError(err.message, undefined);
    }
    const httpStatus = (err as { httpStatus?: number })?.httpStatus;
    throw new HetznerUploadError(
      `Hetzner PUT failed: ${(err as Error)?.message ?? 'error desconocido'}`,
      httpStatus,
    );
  }
}

/**
 * Convenience: requestUploadToken + putToHetzner in one call.
 * Returns the storageKey to be passed to createImage(input.storageKey, ...).
 */
export async function uploadToHetzner(
  blob: Blob,
  input: UploadTokenInput,
  onProgress?: (p: UploadProgress) => void,
): Promise<{ storageKey: string }> {
  const token = await requestUploadToken(input);
  await putToHetzner(token.uploadUrl, blob, input.contentType, onProgress);
  return { storageKey: token.storageKey };
}
